import React from "react";

/**
 * Countdown — live countdown to the event day (15 October, 09:00 CET/CEST
 * Spain time). Rolls over to the next year automatically once the date
 * passes, so the landing never shows a stale/expired timer.
 *
 * Exports:
 *  - useCountdown(): live { days, hours, minutes, seconds, done } object
 *    (used by the Hero for the compact "Faltan X días" line)
 *  - Countdown (default): full editorial 4-unit timer for the Urgency block
 */

const EVENT_TS = (() => {
  const now = new Date();
  // October 15th falls inside CEST (+02:00) — DST ends the last Sunday of Oct.
  let ts = new Date(`${now.getFullYear()}-10-15T09:00:00+02:00`).getTime();
  if (ts <= now.getTime()) {
    ts = new Date(`${now.getFullYear() + 1}-10-15T09:00:00+02:00`).getTime();
  }
  return ts;
})();

const pad = (n) => String(n).padStart(2, "0");

export const useCountdown = () => {
  const calc = React.useCallback(() => {
    const diff = Math.max(0, EVENT_TS - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor(diff / 3600000) % 24,
      minutes: Math.floor(diff / 60000) % 60,
      seconds: Math.floor(diff / 1000) % 60,
      done: diff === 0,
    };
  }, []);

  const [time, setTime] = React.useState(calc);

  React.useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [calc]);

  return time;
};

const UNITS = [
  { key: "days", label: "Días" },
  { key: "hours", label: "Horas" },
  { key: "minutes", label: "Min" },
  { key: "seconds", label: "Seg" },
];

const Countdown = () => {
  const t = useCountdown();

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-8 max-w-2xl"
      data-testid="countdown-timer"
    >
      {UNITS.map(({ key, label }, i) => (
        <div
          key={key}
          className={i > 0 ? "sm:border-l sm:border-[#3F403A]/12 sm:pl-6" : ""}
          data-testid={`countdown-${key}`}
        >
          <p
            className="font-serif text-5xl md:text-6xl lg:text-7xl leading-none text-[#3F403A]"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {key === "days" ? t[key] : pad(t[key])}
          </p>
          <p className="mt-3 overline text-[#3F403A]/55">{label}</p>
        </div>
      ))}
    </div>
  );
};

export default Countdown;
